<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { isBusy } from '../../helpers/pageLoad';

/**
 * The 2px line at the top of the page while something is on its way
 * (prototype `nprog`).
 *
 * Nothing for the first 200ms: most pages are there by then, and a line that
 * flashes for a frame reads as slowness. After that it eases toward 85% and
 * stays short of the end — a full bar that keeps waiting is a lie — and runs
 * to the end and fades only once the page is there.
 *
 * prefers-reduced-motion keeps it, without the travel: it is information,
 * not decoration.
 */
const props = defineProps({
  // The wait before it shows; tests set it to 0.
  delay: { type: Number, default: 200 },
});

const SHOW_AT = 12;
const CEILING = 85;

const shown = ref(false);
const done = ref(false);
const at = ref(0);

let showTimer = null;
let creepTimer = null;
let resetTimer = null;

const clearTimers = () => {
  clearTimeout(showTimer);
  clearInterval(creepTimer);
  clearTimeout(resetTimer);
};

const start = () => {
  clearTimers();
  done.value = false;
  if (shown.value) return;
  at.value = 0;

  showTimer = setTimeout(() => {
    shown.value = true;
    at.value = SHOW_AT;
    // Each step covers a share of what is left, so it slows as it nears 85%.
    creepTimer = setInterval(() => {
      at.value += (CEILING - at.value) * 0.18;
    }, 260);
  }, props.delay);
};

const finish = () => {
  clearTimers();
  if (!shown.value) return;

  done.value = true;
  resetTimer = setTimeout(() => {
    shown.value = false;
    done.value = false;
    at.value = 0;
  }, 420);
};

const busy = computed(isBusy);
watch(busy, (now) => (now ? start() : finish()), { immediate: true });

onBeforeUnmount(clearTimers);
</script>

<template>
  <div
    class="xl-progress-line"
    :class="{ 'is-on': shown, 'is-done': done }"
    :style="{ width: `${done ? 100 : at}%` }"
    role="progressbar"
    aria-label="Loading"
    :aria-hidden="String(!shown)"
    data-testid="progress-line"
  />
</template>

<style scoped>
.xl-progress-line {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 2000;
  height: 2px;
  width: 0;
  opacity: 0;
  pointer-events: none;
  background: linear-gradient(90deg, #5E2F78, #8154A6);
  box-shadow: 0 0 8px rgba(129, 84, 166, 0.5);
  transition: width 0.35s cubic-bezier(0.2, 0.8, 0.2, 1), opacity 0.2s;
}

.xl-progress-line.is-on { opacity: 1; }

.xl-progress-line.is-done {
  opacity: 0;
  transition: width 0.12s linear, opacity 0.3s 0.1s;
}

@media (prefers-reduced-motion: reduce) {
  .xl-progress-line,
  .xl-progress-line.is-done { transition: opacity 0.2s; }
}
</style>
